import { CommonModule } from '@angular/common';
import { makeEnvironmentProviders, ModuleWithProviders, NgModule, Optional, SkipSelf } from '@angular/core';
import { MAT_RIPPLE_GLOBAL_OPTIONS } from '@angular/material/core';
import {
  NbDatepickerModule,
  NbDialogModule,
  NbMenuModule,
  NbSidebarModule,
  NbThemeModule,
  NbToastrModule,
  NbWindowModule,
} from '@nebular/theme';

import { throwIfAlreadyLoaded } from './module-import-guard';
import { LayoutService, PlayerService, SeoService, StateService } from './utils';

import {
  CountryOrderData,
  EarningData,
  ElectricityData,
  NbUserData,
  OrdersChartData,
  OrdersProfitChartData,
  ProfitBarAnimationChartData,
  ProfitChartData,
  SecurityCamerasData,
  SmartTableData,
  SolarData,
  StatsBarData,
  StatsProgressBarData,
  TemperatureHumidityData,
  TrafficBarData,
  TrafficChartData,
  TrafficListData,
  UserActivityData,
  VisitorsAnalyticsData,
} from './data';

import {
  CountryOrderService,
  EarningService,
  ElectricityService,
  MockDataModule,
  OrdersChartService,
  OrdersProfitChartService,
  ProfitBarAnimationChartService,
  ProfitChartService,
  SecurityCamerasService,
  SmartTableService,
  SolarService,
  StatsBarService,
  StatsProgressBarService,
  TemperatureHumidityService,
  TrafficBarService,
  TrafficChartService,
  TrafficListService,
  UserActivityService,
  UserService,
  VisitorsAnalyticsService,
} from './mock';
import { AbService, CurrentThemeService, MetadataServiceDummy, RippleService } from './utils';

const DATA_SERVICES = [
  { provide: NbUserData, useClass: UserService },
  { provide: ElectricityData, useClass: ElectricityService },
  { provide: SmartTableData, useClass: SmartTableService },
  { provide: UserActivityData, useClass: UserActivityService },
  { provide: OrdersChartData, useClass: OrdersChartService },
  { provide: ProfitChartData, useClass: ProfitChartService },
  { provide: TrafficListData, useClass: TrafficListService },
  { provide: EarningData, useClass: EarningService },
  { provide: OrdersProfitChartData, useClass: OrdersProfitChartService },
  { provide: TrafficBarData, useClass: TrafficBarService },
  { provide: ProfitBarAnimationChartData, useClass: ProfitBarAnimationChartService },
  { provide: TemperatureHumidityData, useClass: TemperatureHumidityService },
  { provide: SolarData, useClass: SolarService },
  { provide: TrafficChartData, useClass: TrafficChartService },
  { provide: StatsBarData, useClass: StatsBarService },
  { provide: CountryOrderData, useClass: CountryOrderService },
  { provide: StatsProgressBarData, useClass: StatsProgressBarService },
  { provide: VisitorsAnalyticsData, useClass: VisitorsAnalyticsService },
  { provide: SecurityCamerasData, useClass: SecurityCamerasService },
  { provide: MAT_RIPPLE_GLOBAL_OPTIONS, useExisting: RippleService },
];

export const NB_CORE_PROVIDERS = [
  ...(MockDataModule.forRoot().providers || []),
  ...DATA_SERVICES,
  LayoutService,
  PlayerService,
  SeoService,
  MetadataServiceDummy,
  StateService,
  AbService,
  CurrentThemeService,
];

export const provideNebularCore = (addMock = false) =>
  makeEnvironmentProviders([
    LayoutService,
    PlayerService,
    SeoService,
    MetadataServiceDummy,
    StateService,
    AbService,
    CurrentThemeService,
    addMock
      ? [...(MockDataModule.forRoot().providers || []), ...DATA_SERVICES]
      : [{ provide: MAT_RIPPLE_GLOBAL_OPTIONS, useExisting: RippleService }],
  ]);

@NgModule({
  imports: [
    CommonModule,
    NbSidebarModule.forRoot(),
    NbThemeModule.forRoot(),
    NbMenuModule.forRoot(),
    NbDatepickerModule.forRoot(),
    NbDialogModule.forRoot(),
    NbWindowModule.forRoot(),
    NbToastrModule.forRoot(),
  ],
})
export class CoreModule {
  constructor(@Optional() @SkipSelf() parentModule: CoreModule) {
    throwIfAlreadyLoaded(parentModule, 'CoreModule');
  }

  static forRoot(): ModuleWithProviders<CoreModule> {
    return {
      ngModule: CoreModule,
      providers: [...NB_CORE_PROVIDERS, ...DATA_SERVICES],
    };
  }
}
